import React from "react";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import { DURATION_UNITS } from "../../../../utils/durations";
import { CONTENT_TYPE_ICONS } from "../../../content/contentTypeIcons";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  activityStatusOptions,
  dateColumn,
  durationColumn,
  statusColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { CELL_ICON_CLASS } from "../../../dataTable/dataTableStyles";
import { contentAnalyticsPath } from "../../../routes/routes";

import type { TFunction } from "i18next";

import type {
  ContentAnalyticsRow,
  ContentAnalyticsType,
  ContentTypeDefinition,
} from "./contentAnalyticsColumns.types";
import type {
  GetSumMediaViewsResponse,
  SumMediaViewMediaInfo,
} from "../../../../api/StatsAPI.types";
import type {
  DataTableColumnDef,
  DataTablePage,
} from "../../../dataTable/dataTable.types";

interface ContentAnalyticsColumnsOptions {
  t: TFunction;
  projectSlug: string | undefined;
}

const CONTENT_TYPE_LIST: ContentTypeDefinition[] = [
  {
    value: "video",
    icon: CONTENT_TYPE_ICONS.video,
    tooltipKey: "tooltip_videocam",
  },
  {
    value: "live",
    icon: CONTENT_TYPE_ICONS.live,
    tooltipKey: "tooltip_live_tv",
  },
  {
    value: "replay",
    icon: CONTENT_TYPE_ICONS.replay,
    tooltipKey: "tooltip_replay",
  },
  {
    value: "audio",
    icon: CONTENT_TYPE_ICONS.audio,
    tooltipKey: "tooltip_music_note",
  },
  {
    value: "external",
    icon: CONTENT_TYPE_ICONS.external,
    tooltipKey: "tooltip_movie",
  },
];

function getContentType(media: SumMediaViewMediaInfo): ContentAnalyticsType {
  switch (media.type) {
    case "hosted":
      if (media.audio_only) return "audio";
      return media.is_replay ? "replay" : "video";
    case "live":
      return "live";
    case "external":
      return "external";
    default:
      return "video";
  }
}

export function selectContentAnalyticsPage(
  response: GetSumMediaViewsResponse,
): DataTablePage<ContentAnalyticsRow> {
  return {
    rows: response.data.map(
      ({
        media,
        views,
        viewing_duration: viewingDuration,
        average_viewing_duration: averageViewingDuration,
        last_viewed_date: lastViewedDate,
      }) => ({
        status:
          media.status === "archived" ? media.status : media.publication_status,
        content_type: getContentType(media),
        content_label: media.label,
        content_duration: media.source_duration || 0,
        views,
        viewing_duration: viewingDuration,
        average_viewing_duration: averageViewingDuration,
        date: lastViewedDate,
        media_uuid: media.uuid,
      }),
    ),
    rowCount: response.page.total_items,
  };
}

export function contentAnalyticsFiltersToApi(
  filters: Record<string, string>,
): Record<string, unknown> {
  const apiFilter: Record<string, unknown> = {};

  if (filters.status) apiFilter.status = filters.status;

  switch (filters.content_type) {
    case "video":
      apiFilter.type = "hosted";
      apiFilter.audio_only = false;
      break;
    case "replay":
      apiFilter.type = "hosted";
      apiFilter.replay = true;
      break;
    case "audio":
      apiFilter.type = "hosted";
      apiFilter.audio_only = true;
      break;
    case "live":
      apiFilter.type = "live";
      break;
    case "external":
      apiFilter.type = "external";
      break;
    default:
      break;
  }

  return apiFilter;
}

export function getContentAnalyticsHref(
  row: ContentAnalyticsRow,
  projectSlug: string | undefined,
): string | undefined {
  return contentAnalyticsPath(projectSlug, row.media_uuid);
}

export function contentAnalyticsColumns({
  t,
  projectSlug,
}: ContentAnalyticsColumnsOptions): DataTableColumnDef<ContentAnalyticsRow>[] {
  return [
    statusColumn<ContentAnalyticsRow>("status", t("content_status"), [
      ...activityStatusOptions(t),
      { value: "archived", label: t("common:archived"), tone: "neutral" },
    ]),
    {
      accessorKey: "content_type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("type")} />
      ),
      enableSorting: false,
      meta: {
        label: t("type"),
        width: "9%",
        minWidth: 50,
        align: "center",
        filterOptions: CONTENT_TYPE_LIST.map(
          ({ value, icon: TypeIcon, tooltipKey }) => ({
            value,
            label: t(`editor-content:${tooltipKey}`),
            icon: <TypeIcon />,
          }),
        ),
      },
      cell: ({ row }) => {
        const definition = CONTENT_TYPE_LIST.find(
          ({ value }) => value === row.original.content_type,
        );
        if (!definition) return null;
        const label = t(`editor-content:${definition.tooltipKey}`);
        return (
          <span
            className="inline-flex align-middle"
            data-tooltip-content={label}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <definition.icon className={CELL_ICON_CLASS} />
            <span className="sr-only">{label}</span>
          </span>
        );
      },
    },
    {
      accessorKey: "content_label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("content_label")} />
      ),
      meta: { label: t("content_label"), searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getContentAnalyticsHref(row.original, projectSlug)}
        >
          {row.original.content_label}
        </DataTableLinkCell>
      ),
    },
    durationColumn<ContentAnalyticsRow>(
      "content_duration",
      t("content_duration"),
      DURATION_UNITS.MILLISECONDS,
      "fastDuration",
      { width: "9%", minWidth: 110, align: "center" },
    ),
    {
      accessorKey: "views",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("views")} />
      ),
      meta: {
        label: t("views"),
        width: "11%",
        minWidth: 135,
        align: "center",
      },
    },
    durationColumn<ContentAnalyticsRow>(
      "viewing_duration",
      t("viewing_duration"),
      DURATION_UNITS.SECONDS,
      "humanised",
      { width: "16%", minWidth: 200, align: "center" },
    ),
    durationColumn<ContentAnalyticsRow>(
      "average_viewing_duration",
      t("average_viewing_duration"),
      DURATION_UNITS.SECONDS,
      "humanised",
      { width: "20%", minWidth: 245, align: "center" },
    ),
    dateColumn<ContentAnalyticsRow>("date", t("date"), {
      minWidth: 150,
    }),
  ];
}
